import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "flex justify-flex-start items-center" }
const _hoisted_2 = { class: "mr-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_3 = { class: "flex justify-between" }
const _hoisted_4 = { class: "flex" }

import { computed, ref, reactive, watch, onMounted, nextTick } from "vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import { DialogTitle } from "@headlessui/vue"
import { ClipboardCheckIcon } from "@heroicons/vue/outline"
import { useTaskStore } from "@/stores/task"
import { useAgentsStore } from "@/stores/agents"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useAuthStore } from "@/stores/auth"
import { useAgencyStore } from "@/stores/agency"
import { ToastService } from "@/shared/services/toast"

import { deleteEmptyKeys, isAdmin, getDomainId, getTaskTypes, getTaskReminderOptions } from "@/shared/utils/helpers"
import { formatters, getters } from "@/shared/utils/dateUtils"

import { Task } from "@/shared/models/task"

import CreateTaskForm from "@/components/tasks/CreateTaskForm.vue"

interface Props {
    isCreateTaskModalOpen: boolean
    modalAction: string
    taskId: string
    contactId?: string
    isNotEditableByUser: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateTask',
  props: {
    isCreateTaskModalOpen: { type: Boolean },
    modalAction: {},
    taskId: {},
    contactId: {},
    isNotEditableByUser: { type: Boolean }
  },
  emits: ["onDeleteTask", "onCreateTaskModalClose", "onCreateTask"],
  setup(__props: any, { emit: __emit }) {

const toastService = new ToastService()
const taskStore = useTaskStore()
const agentStore = useAgentsStore()
const authStore = useAuthStore()
const agencyStore = useAgencyStore()
const gridCollectionStore = useGridCollectionStore()

const props = __props
const emit = __emit

const error = ref()
const isTaskLoading = ref(false)
const queueDate = ref("")
const queueTime = ref("")

const taskTypes = ref(getTaskTypes())
const reminderOptions = ref(getTaskReminderOptions())

const cachedAgents = computed(() => agentStore.tableState.data)
const currentUser = computed(() => authStore.currentUser)
const isModalActionCreate = computed(() => props.modalAction === "create")
const actionButtonText = computed(() => {
    if (!isSubmitting.value) {
        return isModalActionCreate.value ? "Create task" : "Save"
    }
    return isModalActionCreate.value ? "Creating task..." : "Saving task..."
})
const modalHeaderText = computed(() => (isModalActionCreate.value ? "Create" : "Edit"))

const isSubmitting = ref(false)

const task = reactive({
    _id: "",
    displayName: "",
    description: "",
    when: {
        startAt: "",
        timezone: "America/Los_Angeles",
    },
    reminder: reminderOptions.value[0].attribute,
    shouldMarkAsMissedIfNotCompleted: true,
    agent: {
        _id: currentUser?.value?._id,
        fullName: currentUser?.value?.fullName,
    },
    contactId: "",
    agentId: currentUser?.value?._id,
    collection: {
        _id: "",
        displayName: "",
    },
    type: "",
    collectionId: "",
    status: "",
    notes: "",
    domainId: "",
    unitsOfTimeToRemove: "30",
    typeOfTimeToRemove: "days",
})

const domainOptions = computed(() => agencyStore.agency.agencyDomains)
const selectedDomain = ref(initializeDomain())

function handleEnterKey(event: KeyboardEvent) {
    if (event.key === "Enter" && props.isCreateTaskModalOpen) {
        addTask()
    }
}

function resetTaskObject() {
    task._id = ""
    task.displayName = ""
    task.description = ""
    task.when = {
        startAt: "",
        timezone: "America/Los_Angeles",
    }
    task.reminder = reminderOptions.value[0].attribute
    task.shouldMarkAsMissedIfNotCompleted = true
    task.agent = {
        _id: currentUser.value._id,
        fullName: currentUser.value.fullName,
    }
    task.contactId = ""
    task.agentId = currentUser.value._id
    task.type = ""
    task.collection = {
        _id: "",
        displayName: "",
    }
    task.collectionId = ""
    task.status = ""
    task.notes = ""
    selectedDomain.value = initializeDomain()
    setQueueDateAndTime(new Date())
}

function taskUpdated(updatedTask: Task) {
    Object.assign(task, updatedTask)
}

function closeCreateTaskModal() {
    error.value = null
    resetTaskObject()
    emit("onCreateTaskModalClose", false)
}

function setQueueDateAndTime(date: Date) {
    queueDate.value = getters.getDateInputFormat(date)
    queueTime.value = getters.getTimeInputFormat(date)
}

async function validateTask(task: Task) {
    const isPastTime = checkIfQueueTimeIsInPast(task.when.startAt)
    if (isPastTime) {
        await nextTick(() => {
            error.value = {
                field: "when.startat",
                message: "Task time cannot be in the past.",
            }
        })
        isSubmitting.value = false
        return false
    }
    // setTaskStartDate()

    if (!task.domainId) {
        await nextTick(() => {
            error.value = {
                field: "domain",
                message: "Domain is required. Please select a domain.",
            }
        })
        isSubmitting.value = false
        return false
    }
    return true
}

async function addTask() {
    const isCreateTask = props.modalAction === "create"
    isSubmitting.value = true
    try {
        error.value = undefined

        const nonEmptyObj = deleteEmptyKeys(task)
        if (props.contactId) {
            nonEmptyObj.contactId = props.contactId
        }

        let toastMessage = ""
        if (await validateTask(nonEmptyObj)) {
            // Convert when.startAt from agency's timezone to UTC
            if (nonEmptyObj.when && nonEmptyObj.when.startAt) {
                nonEmptyObj.when.startAt = formatters.formatDateToUtc(nonEmptyObj.when.startAt)
            }

            if (isCreateTask) {
                localStorage.setItem("taskDomainId", task.domainId ?? selectedDomain.value)
                await taskStore.createTask(nonEmptyObj)
                toastMessage = "A new task was created."
                localStorage.removeItem("taskDomainId")
            } else {
                nonEmptyObj.description = task.description ?? ""
                nonEmptyObj.notes = task.notes ?? ""
                await taskStore.updateTask(props.taskId, nonEmptyObj)
                toastMessage = "Task was successfully updated."
            }

            toastService.addToast({
                type: "success",
                message: toastMessage,
            })

            emit("onCreateTask")

            gridCollectionStore.collectionType = "task"
            await Promise.all([taskStore.getActiveTaskSession(), gridCollectionStore.getCollections()])

            closeCreateTaskModal()
        }
    } catch (err) {
        error.value = err
        localStorage.removeItem("taskDomainId")
    }
    isSubmitting.value = false
}

async function deleteTaskFromModal() {
    await taskStore.deleteOne(props.taskId)

    toastService.addToast({
        type: "success",
        message: "Task was deleted.",
    })

    closeCreateTaskModal()
    taskStore.getTasks()
    emit("onDeleteTask")
}

function initializeDomain() {
    const domain = getDomainId()
    return domain === "all" ? null : domain
}

function checkIfQueueTimeIsInPast(dateString: string) {
    const taskDate = getters.getNewDate(dateString)
    const now = new Date()
    return taskDate < now
}

onMounted(async () => {
    gridCollectionStore.collectionType = "task"
    if (isAdmin() && !cachedAgents.value.length) {
        await agentStore.loadAgents()
    }
    setQueueDateAndTime(new Date())
})

watch(
    () => props.isCreateTaskModalOpen,
    async () => {
        if (props.modalAction === "edit" && props.isCreateTaskModalOpen) {
            try {
                isTaskLoading.value = true
                const response = await taskStore.getTask(props.taskId)
                task.displayName = response.task.displayName
                task.description = response.task.description
                task.when = {
                    startAt: response.task.when?.startAt,
                    timezone: response.task.when?.timezone || "America/Los_Angeles",
                }
                task.reminder =
                    reminderOptions.value.find((option) => option.attribute === response.task.reminder)?.attribute ||
                    "minutes_30"
                task.shouldMarkAsMissedIfNotCompleted = response.task.shouldMarkAsMissedIfNotCompleted || false
                task.agent = {
                    _id: response.task.agent?._id ? response.task.agent?._id : currentUser.value._id,
                    fullName: response.task.agent?.fullName,
                }
                task.agentId = response.task.agent?._id ? response.task.agent?._id : currentUser.value._id
                task.type = taskTypes.value.find((option) => option.attribute === response.task.type)?.attribute || ""
                task.collection = {
                    _id: response.task.collection?._id,
                    displayName: response.task.collection?.displayName,
                }
                task.collectionId = response.task.collection?._id
                task.status = response.task.status
                task.notes = response.task.notes
                task.domainId = response.task.domainId || ""
                selectedDomain.value = domainOptions.value.find((option) => option._id === task?.domainId)?._id
                setQueueDateAndTime(getters.getNewDate(task.when.startAt))
            } catch (e) {
                console.log(`There was an error getting task with id: ${props.taskId}`, e)
            } finally {
                isTaskLoading.value = false
            }
        }
    }
)

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GgmsModal, {
    open: _ctx.isCreateTaskModalOpen,
    onCloseModal: closeCreateTaskModal,
    onKeydown: _withKeys(_withModifiers(handleEnterKey, ["prevent"]), ["enter"])
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(ClipboardCheckIcon), {
            class: "h-6 w-6 text-primary-color-600",
            "aria-hidden": "true"
          })
        ]),
        _createVNode(_unref(DialogTitle), {
          as: "h3",
          class: "text-lg leading-6 font-medium text-gray-900"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(modalHeaderText.value) + " Task ", 1)
          ]),
          _: 1
        })
      ])
    ]),
    under: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", null, [
          (!isModalActionCreate.value)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 0,
                styleType: "active",
                disabled: _ctx.isNotEditableByUser,
                classes: [
                            'text-red-600 bg-transparent border-transparent shadow-transparent focus:ring-red-600/50',
                        ],
                onClick: _cache[0] || (_cache[0] = ($event: any) => (deleteTaskFromModal()))
              }, {
                default: _withCtx(() => _cache[1] || (_cache[1] = [
                  _createTextVNode("Delete ")
                ])),
                _: 1,
                __: [1]
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(GgmsButton, {
            styleType: "secondary",
            onClick: closeCreateTaskModal
          }, {
            default: _withCtx(() => _cache[2] || (_cache[2] = [
              _createTextVNode("Cancel")
            ])),
            _: 1,
            __: [2]
          }),
          _createVNode(GgmsButton, {
            class: "ml-3",
            disabled: isSubmitting.value || _ctx.isNotEditableByUser,
            onClick: addTask
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(actionButtonText.value), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      (_ctx.isCreateTaskModalOpen)
        ? (_openBlock(), _createBlock(CreateTaskForm, {
            key: 0,
            errorValue: error.value,
            isForWorkflow: false,
            modalAction: _ctx.modalAction,
            isCreateTaskModalOpen: _ctx.isCreateTaskModalOpen,
            isUpdateTask: !isModalActionCreate.value,
            "onUpdate:task": taskUpdated,
            taskId: _ctx.taskId
          }, null, 8, ["errorValue", "modalAction", "isCreateTaskModalOpen", "isUpdateTask", "taskId"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["open", "onKeydown"]))
}
}

})